import React, { useEffect, useState } from "react";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

/* ---- Loyalty points redemption chip -------------------------------------
   Shown under the client name in the OrderDetail header. Click → tiny
   inline prompt asks how many points to redeem. Backend applies them as
   an invoice-wide discount (1 pt = 1,000 IDR, split proportionally across
   non-gift-card lines). The customer's balance is only debited when the
   order actually completes. */
export default function LoyaltyPointsPanel({ order, customer, branch, onChanged }) {
  const [open, setOpen] = useState(false);
  const [pts, setPts] = useState("");
  const [busy, setBusy] = useState(false);
  const [availInfo, setAvailInfo] = useState(null);
  const isClosed = order.status === "voided" || order.status === "refunded" || order.status === "completed" || order.status === "merged" || order.status === "closed_no_show";
  const balance = Math.max(0, Number(customer?.loyalty_points || 0));
  const staged = Math.max(0, Number(order?.loyalty_points_used || 0));
  const idrPerPoint = Math.max(1, Number(availInfo?.idr_per_point || 1000));
  const stagedRp = staged * idrPerPoint;
  // Effective max the operator can stage on THIS order right now.
  // Falls back to `balance` while the /available call is in flight, so the
  // panel still renders. Once loaded, it deducts any points reserved on
  // other open invoices for the same guest — the fix that closes the
  // double-spend loophole.
  const reservedElsewhere = Math.max(0, Number(availInfo?.reserved_elsewhere || 0));
  const maxRedeemable = availInfo
    ? Math.max(0, Number(availInfo.available || 0))
    : Math.max(0, balance - reservedElsewhere);
  // Gating flags returned by /loyalty/available (member-only + branch
  // opt-in). Falls back to the customer's is_member flag while the
  // fetch is in flight so the button is instantly locked for walk-ins.
  const isMember = availInfo ? !!availInfo.is_member : !!customer?.is_member;
  const branchLoyaltyEnabled = availInfo ? !!availInfo.branch_loyalty_enabled : true;
  const lockReason = availInfo?.lock_reason || (
    !order.customer_id ? "Redemption is only available on invoices with a customer on file." :
    !isMember ? "This guest is not a Member — only Members can redeem loyalty points." :
    !branchLoyaltyEnabled ? "Loyalty redemption is disabled for this branch." :
    null
  );
  const redeemLocked = isClosed || !!lockReason;

  // Fetch the effective-available snapshot when the panel mounts and
  // whenever the order or staged points change. Also on dialog open so
  // reservations from other tabs are reflected. This lets us render
  // the correct lock state (member / branch-enabled) even before the
  // dialog is ever opened.
  useEffect(() => {
    let cancelled = false;
    if (open) setPts(String(staged || ""));
    (async () => {
      try {
        const r = await staffApi.get(`/orders/${order.id}/loyalty/available`);
        if (!cancelled) setAvailInfo(r.data);
      } catch { if (!cancelled) setAvailInfo(null); }
    })();
    return () => { cancelled = true; };
  }, [open, order.id, staged, balance]);

  const submit = async () => {
    const val = Math.max(0, parseInt(String(pts).replace(/\D/g, "") || "0", 10));
    if (val > maxRedeemable) {
      if (reservedElsewhere > 0) {
        toast.error(`Only ${maxRedeemable} pts available — ${reservedElsewhere} pts are already reserved on other open invoices.`);
      } else {
        toast.error(`Only ${maxRedeemable} pts available.`);
      }
      return;
    }
    setBusy(true);
    try {
      await staffApi.post(`/orders/${order.id}/loyalty/redeem`, { points: val });
      toast.success(val > 0 ? `Redeemed ${val} pts (-${formatBranchMoney(branch, val * idrPerPoint)})` : "Loyalty redemption cleared");
      // Broadcast so the header customerDetail and the customer's
      // own portal (if signed in on a peer device) refresh their
      // loyalty balance without a manual reload.
      try {
        window.dispatchEvent(new CustomEvent("cocoon:customer-updated", { detail: { customer_id: order.customer_id } }));
        window.dispatchEvent(new CustomEvent("cocoon:loyalty-updated", { detail: { customer_id: order.customer_id } }));
      } catch {}
      setOpen(false);
      await onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Always render the pill so operators see the lock chip for
  // Non-Members even when the guest has 0 points. Only skip entirely
  // when the invoice is closed AND nothing was ever staged.
  if (balance <= 0 && staged <= 0 && isClosed) {
    return (
      <div className="mt-2 text-[11px] text-muted-foreground" data-testid="loyalty-panel-empty">
        Loyalty: 0 pts
      </div>
    );
  }

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => !redeemLocked && setOpen(true)}
        disabled={redeemLocked}
        className={`inline-flex items-center gap-2 text-xs rounded-full px-3 py-1 border transition-colors ${redeemLocked ? "bg-muted/50 text-muted-foreground border-border cursor-not-allowed" : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"}`}
        data-testid="loyalty-panel-btn"
        title={isClosed ? "" : (lockReason || "Click to redeem points on this invoice")}
      >
        <span className="font-semibold" data-testid="loyalty-balance">{balance.toLocaleString()} pts</span>
        <span className="text-[10px] opacity-70">·</span>
        <span className="text-[10px]">{formatBranchMoney(branch, balance * idrPerPoint)} available</span>
        {staged > 0 && (
          <span className="ml-1 text-[10px] font-medium bg-amber-200/70 rounded-full px-2 py-0.5" data-testid="loyalty-staged">Using {staged} pts</span>
        )}
        {redeemLocked && !isClosed && (
          <span className="ml-1 text-[10px] font-medium bg-slate-200/70 rounded-full px-1.5 py-0.5" data-testid="loyalty-redeem-locked">Redeem locked</span>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="loyalty-redeem-dialog">
          <DialogHeader>
            <DialogTitle>Redeem loyalty points</DialogTitle>
            <DialogDescription>1 point = {formatBranchMoney(branch, idrPerPoint)} · applied before service charge and tax, split proportionally across treatments on this bill.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-1">
            <div className="text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Available to redeem:</span><span className="font-medium" data-testid="loyalty-available">{maxRedeemable} pts</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Currently staged:</span><span>{staged} pts · {formatBranchMoney(branch, stagedRp)}</span></div>
              {reservedElsewhere > 0 && (
                <div className="mt-1 text-[11px] text-amber-700" data-testid="loyalty-reserved-note">
                  {reservedElsewhere} pts reserved on other open invoices for this guest.
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Points to use</Label>
              <Input
                type="text"
                inputMode="numeric"
                value={pts}
                onChange={(e) => setPts(e.target.value.replace(/\D/g, ""))}
                placeholder="e.g. 200"
                data-testid="loyalty-redeem-input"
                autoFocus
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Discount preview: <span className="font-medium">{formatBranchMoney(branch, Math.max(0, parseInt(pts || "0", 10)) * idrPerPoint)}</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            {staged > 0 && <Button variant="outline" onClick={() => { setPts("0"); }} disabled={busy} data-testid="loyalty-redeem-clear">Clear</Button>}
            <Button onClick={submit} disabled={busy} data-testid="loyalty-redeem-ok">OK</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
